import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { Building2, Eye, EyeOff, GraduationCap, Lock, Mail, Presentation } from "lucide-react";
import apiClient from "../../utils/apiClient";
import InstitutePicker from "./InstitutePicker";
import RoleTiles from "./RoleTiles";
import { AppButton, Callout, Checkbox, FormField, IconButton, Input, Label, PhoneInput } from "../ui";

const ROLES = [
  { key: "student", label: "Student", icon: GraduationCap },
  { key: "instructor", label: "Instructor", icon: Presentation },
  { key: "admin", label: "Admin", icon: Building2 },
];

// Which endpoint each role signs up against, and what it says on success. The
// three branches were an if/else chain that differed only in these two strings.
const SIGNUP_ENDPOINTS = {
  student: { url: "/auth/student-signup", done: "Registration successful! Please login." },
  instructor: { url: "/auth/instructor-signup", done: "Registration successful as instructor! Please login." },
  admin: { url: "/auth/admin-signup", done: "Registration successful as admin! Please login." },
};

const Signup = () => {
  const { search } = useLocation();

  const [form, setForm] = useState({
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    role: "student",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ tone: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [institutes, setInstitutes] = useState(null); // multi-institute picker

  // Prefill from an invitation link (?email=&mobile=&role=).
  useEffect(() => {
    const query = new URLSearchParams(search);
    const email = query.get("email");
    const mobile = query.get("mobile");
    const role = query.get("role");
    setForm((prev) => ({
      ...prev,
      email: email || prev.email,
      mobile: mobile || prev.mobile,
      role: SIGNUP_ENDPOINTS[role] ? role : prev.role,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mismatch = Boolean(form.confirmPassword) && form.password !== form.confirmPassword;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setMsg({ tone: "", text: "" });
  };

  // PhoneInput hands back the number in its stored shape, not an event.
  const setMobile = (mobile) => {
    setForm((prev) => ({ ...prev, mobile }));
    setMsg({ tone: "", text: "" });
  };

  // Core signup call — extracted so the institute picker can re-invoke it with
  // the chosen instituteId.
  const doSignup = async (extraPayload = {}) => {
    setLoading(true);
    setMsg({ tone: "", text: "" });
    try {
      const { url, done } = SIGNUP_ENDPOINTS[form.role];
      await apiClient.post(url, {
        email: form.email,
        mobile: form.mobile,
        password: form.password,
        role: form.role,
        ...extraPayload,
      });
      setMsg({ tone: "success", text: done });
      if (form.role === "student") {
        setForm({ email: "", mobile: "", password: "", confirmPassword: "", role: "student" });
        setAcceptedTerms(false);
      }
      setInstitutes(null);
    } catch (err) {
      const resp = err?.response;
      // HTTP 300 — the identifier exists at more than one institute.
      if (resp?.status === 300 && resp?.data?.multiInstitute) {
        setInstitutes(resp.data.institutes);
      } else {
        setMsg({ tone: "danger", text: resp?.data?.message || "Signup failed!" });
      }
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ tone: "", text: "" });

    if (!acceptedTerms) {
      setMsg({ tone: "danger", text: "Please accept the Terms & Conditions to continue." });
      return;
    }
    if (form.password !== form.confirmPassword) {
      setMsg({ tone: "danger", text: "Passwords do not match." });
      return;
    }

    await doSignup();
  };

  const handleInstitutePick = (inst) => {
    setInstitutes(null);
    doSignup({ instituteId: inst.instituteId });
  };

  return (
    <>
      <div>
        <header className="tw-mb-7 tw-text-center">
          <h1 className="tw-mb-1.5 tw-text-[1.75rem] tw-font-bold tw-tracking-[-0.02em] tw-text-content">
            Create your account
          </h1>
          <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
            Start your learning journey today
          </p>
        </header>

        <div className="tw-mb-5">
          <Label id="signup-role-label" className="tw-mb-2 tw-block">
            I want to join as
          </Label>
          <RoleTiles
            roles={ROLES}
            value={form.role}
            onSelect={(role) => setForm((prev) => ({ ...prev, role: role.key }))}
            columns={3}
            labelId="signup-role-label"
          />
        </div>

        <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
          <FormField label="Email Address" htmlFor="signup-email">
            <Input
              id="signup-email"
              size="lg"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="Enter your email"
              value={form.email}
              onChange={handleChange}
              required
              leading={<Mail size={16} />}
            />
          </FormField>

          <FormField label="Mobile Number" htmlFor="signup-mobile">
            <PhoneInput
              id="signup-mobile"
              size="lg"
              name="mobile"
              placeholder="Enter your mobile number"
              value={form.mobile}
              onChange={setMobile}
              required
            />
          </FormField>

          <FormField label="Password" htmlFor="signup-password">
            <Input
              id="signup-password"
              size="lg"
              type={showPassword ? "text" : "password"}
              name="password"
              autoComplete="new-password"
              placeholder="Create a password"
              minLength={5}
              value={form.password}
              onChange={handleChange}
              required
              leading={<Lock size={16} />}
              trailing={
                <IconButton
                  size="sm"
                  label={showPassword ? "Hide password" : "Show password"}
                  icon={showPassword ? Eye : EyeOff}
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                />
              }
            />
          </FormField>

          <FormField
            label="Confirm Password"
            htmlFor="signup-confirm"
            error={mismatch ? "Passwords do not match" : null}
          >
            <Input
              id="signup-confirm"
              size="lg"
              type={showConfirm ? "text" : "password"}
              name="confirmPassword"
              autoComplete="new-password"
              placeholder="Re-enter your password"
              minLength={5}
              value={form.confirmPassword}
              onChange={handleChange}
              required
              invalid={mismatch}
              leading={<Lock size={16} />}
              trailing={
                <IconButton
                  size="sm"
                  label={showConfirm ? "Hide password" : "Show password"}
                  icon={showConfirm ? Eye : EyeOff}
                  onClick={() => setShowConfirm((v) => !v)}
                  tabIndex={-1}
                />
              }
            />
          </FormField>

          <Checkbox
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            label={
              <>
                I agree to the{" "}
                <Link to="/terms-and-conditions" target="_blank" className="tw-text-primary">
                  Terms &amp; Conditions
                </Link>{" "}
                and{" "}
                <Link to="/terms-and-conditions" target="_blank" className="tw-text-primary">
                  Privacy Policy
                </Link>
              </>
            }
          />

          {msg.text && (
            <Callout tone={msg.tone} role="alert">
              {msg.text}
            </Callout>
          )}

          <AppButton
            type="submit"
            size="lg"
            className="tw-h-12 tw-w-full"
            disabled={loading || !acceptedTerms}
          >
            {loading ? (
              <>
                <Spinner size="sm" animation="border" /> Creating account…
              </>
            ) : (
              "Create Account"
            )}
          </AppButton>
        </form>

        <p className="tw-mb-0 tw-mt-6 tw-text-center tw-text-sm tw-text-content-muted">
          Already have an account?{" "}
          <Link
            to="/login"
            className="tw-font-semibold tw-text-primary tw-no-underline hover:tw-underline"
          >
            Sign in instead
          </Link>
        </p>
      </div>

      {/* Multi-Institute Picker */}
      {institutes && (
        <InstitutePicker
          institutes={institutes}
          onSelect={handleInstitutePick}
          onCancel={() => setInstitutes(null)}
          loading={loading}
          title="Choose Your Institute"
        />
      )}
    </>
  );
};

export default Signup;
